import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { type WorkspaceInput } from '@/services/workspace.service';
import { fetchWorkspaceById, updateWorkspace } from '@/features/workspace/workspaceThunks';
import { Button } from '@/components/ui/button';
import Loader from '@/components/ui/Loader';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import Title from '@/components/ui/Title';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { getTimezoneOptions } from '@/lib/timezones';
import {
  API_LANGUAGES,
  APP_LANGUAGES,
  getAppLanguageFromApi,
  type AppLanguageCode,
} from '@/i18n/languages';
import { getWorkspaceLanguage, type WorkspaceLanguage } from '@/i18n/workspaceLanguage';

type WorkspaceFormData = {
  name: string;
  language?: WorkspaceLanguage;
  timezone: string;
};

const LANGUAGE_OPTION_KEY: Record<AppLanguageCode, string> = {
  fr: 'french',
  en: 'english',
  es: 'spanish',
  pt: 'portuguese',
};

export default function GeneralSection() {
  const { i18n, t } = useTranslation('settings');
  const dispatch = useAppDispatch();
  const workspaceId = useAppSelector((state) => state.workspace.currentWorkspace?.id);
  const isLoading = useAppSelector((state) => state.workspace.loading);
  const timezoneOptions = useMemo(() => getTimezoneOptions(), []);

  const workspaceSchema = useMemo(
    () =>
      z.object({
        name: z
          .string()
          .min(1, t('general.validation.nameRequired'))
          .max(160, t('general.validation.nameMaxLength')),
        language: z.enum(API_LANGUAGES).optional(),
        timezone: z
          .string()
          .min(1, t('general.validation.timezoneRequired'))
          .max(64, t('general.validation.timezoneMaxLength')),
      }),
    [t],
  );

  const form = useForm<WorkspaceFormData>({
    resolver: zodResolver(workspaceSchema),
    defaultValues: {
      name: '',
      language: 'FRENCH',
      timezone: 'Europe/Paris',
    },
  });

  useEffect(() => {
    if (!workspaceId) return;

    const fetchWorkspace = async () => {
      const result = await dispatch(fetchWorkspaceById(workspaceId));
      if (fetchWorkspaceById.fulfilled.match(result)) {
        const data = result.payload;
        form.reset({
          name: data.name,
          language: getWorkspaceLanguage(i18n.language),
          timezone: data.timezone,
        });
      } else {
        toast.error(t('general.toast.loadError'));
      }
    };

    fetchWorkspace();
  }, [workspaceId, dispatch, form, i18n.language, t]);

  useEffect(() => {
    form.setValue('language', getWorkspaceLanguage(i18n.language));
  }, [i18n.language, form]);

  const onSubmit = async (data: WorkspaceFormData) => {
    if (!workspaceId) return;

    const payload: WorkspaceInput = {
      name: data.name,
      language: data.language,
      timezone: data.timezone,
    };

    const result = await dispatch(updateWorkspace({ workspaceId, input: payload }));
    if (updateWorkspace.fulfilled.match(result)) {
      const updatedData = result.payload;
      void i18n.changeLanguage(getAppLanguageFromApi(data.language).code);
      toast.success(t('general.toast.success'));
      form.reset({
        name: updatedData.name,
        language: data.language,
        timezone: updatedData.timezone,
      });
    } else {
      toast.error(t('general.toast.updateError'));
    }
  };

  if (isLoading && !form.getValues('name')) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader />
      </div>
    );
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
      <Title level={2}>{t('general.title')}</Title>

      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <Controller
          name="language"
          control={form.control}
          render={({ field }) => (
            <Select value={field.value || 'FRENCH'} onValueChange={field.onChange}>
              <SelectTrigger label={t('general.language')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {APP_LANGUAGES.map(({ code, apiValue }) => (
                  <SelectItem key={code} value={apiValue}>
                    {t(`general.languageOptions.${LANGUAGE_OPTION_KEY[code]}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />

        <Controller
          name="timezone"
          control={form.control}
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger label={t('general.timezone')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {timezoneOptions.map((tz) => (
                  <SelectItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </div>

      <Button type="submit" disabled={isLoading} className="w-full">
        {isLoading && <Loader />}
        {t('general.save')}
      </Button>
    </form>
  );
}
